import {
  SNC_TOOLTIP_BY_SCREEN,
  sncTooltipGroupKey,
} from "../constants/SnCTooltipInformation";

/**
 * Display-only screen labels for Tooltip Admin.
 * API keys (groupKey / fieldKey) stay unchanged.
 */

const SNC_GROUP_SCREEN_LABEL: Record<string, string> = Object.fromEntries(
  Object.keys(SNC_TOOLTIP_BY_SCREEN).map((screen) => [
    sncTooltipGroupKey(screen),
    screen,
  ]),
);

/** One groupKey → one navbar screen (Versa, Retail, Reserve, …). */
export const TOOLTIP_GROUP_SCREEN_LABEL: Record<string, string> = {
  // Versa
  buildingCodesTooltip: "Building",
  roomFeaturesTooltip: "Room Feature",
  roomLocationTooltip: "Room Location",
  housekeepingTooltip: "HouseKeeping Sections",
  roomMaintainenceTooltip: "Room Type Maintenance",
  roomDefinitionsTooltip: "Room Definition",
  departmentsTooltip: "Department",
  categoryTooltip: "Category",
  taxesTooltip: "Tax",
  taxExemptCategoryTooltip: "Tax Exempt Categories",
  guestTypeTooltip: "Guest Type",
  postTypesTooltip: "Post Type",
  originsTooltip: "Origins",
  paymentMethodsTooltip: "Payment Method",
  transportationTooltip: "Transportation",
  customerSegmentsTooltip: "Customer Segments",

  // Retail
  retailCategoryGroupsTooltip: "Category Groups",
  retailRetailSubCategoriesTooltip: "Retail Sub Categories",
  retailCategoryLinkingTooltip: "Category Linking",
  retailRetailCategoriesTooltip: "Retail Categories",
  retailOutletSetupTooltip: "Outlet Setup",
  retailQuickSaleCategoriesTooltip: "Quick Sale Categories",
  retailDiscountTypesTooltip: "Discount Types",
  retailDiscountReasonsTooltip: "Discount Reasons",
  retailVendorTypesTooltip: "Vendor Types",
  retailVendorSetupTooltip: "Vendor Setup",
  retailRetailItemsTooltip: "Retail Items",
  retailTaxesTooltip: "Taxes",
  retailPaymentMethodsTooltip: "Payment Methods",
  retailRoleSetupTooltip: "Role Setup",
  retailUserSetupTooltip: "User Setup",

  // Reserve
  reserveSectionTooltip: "Reserve",
  reserveOutletSetupTooltip: "Outlet Setup",
  reserveLocationTooltip: "Location",
  reserveTablesTooltip: "Tables",
  reserveReservationGuestNotesTooltip: "Reservation Guest Notes",
  reserveRowFieldTooltip: "List fields",
  reserveRolesTooltip: "Roles",
  reserveUserDetailsTooltip: "User Details",
  reserveUserPermissionsTooltip: "User Permissions",
  reserveServiceChargeTooltip: "Service Charge",
  reserveTaxesTooltip: "Taxes",
  reserveInstructorsTooltip: "Instructors",
  reserveMiscItemsTooltip: "Misc Items",
  reserveEquipmentsAddonsTooltip: "Equipments/Add-ons",
  reserveActivitiesTooltip: "Activities",
  reserveOperationHoursTooltip: "Operation Hours",
  reserveRatePlanTooltip: "Rate Plan",
  reserveReservationSettingsTooltip: "Reservation Settings",
  reservePropertyQuestionsTooltip: "Property Questions",
  reservePropertyInformationTooltip: "Property Information",
  reserveListAvailableTooltip: "List available",
  ...SNC_GROUP_SCREEN_LABEL,
};

/** Versa Segment 1 vs Segment 2 share one group object. */
const VERSA_SEGMENT_SCREEN_BY_FIELD: Record<string, string> = {
  header1: "Segment 1",
  segment1Code: "Segment 1",
  segment1Name: "Segment 1",
  routeLocalCalls: "Segment 1",
  systemOptions: "Segment 1",
  guestTypeCode: "Segment 1",
  active1: "Segment 1",
  header2: "Segment 2",
  segment2Code: "Segment 2",
  segment2Name: "Segment 2",
  parentSegment1: "Segment 2",
  guestTypeCode2: "Segment 2",
  active2: "Segment 2",
};

/** Primary screen for each golfTooltipHeaders field (Golf / Spa POS / SnC). */
export const GOLF_TOOLTIP_SCREEN_BY_FIELD: Record<string, string> = {
  outletName: "Outlet Setup",
  active: "Multiple screens",
  terminalID: "Outlet Setup",
  profitCenterId: "Outlet Setup",
  autoPrintReceipts: "Outlet Setup",
  courseName: "Courses",
  defaultOutlet: "Courses",
  hole9: "Courses",
  hole18: "Courses",
  allocationBlockCode: "Allocation Blocks",
  allocationBlockName: "Allocation Blocks",
  allocationBlockColor: "Allocation Blocks",
  daysOut: "Allocation Blocks",
  allocationTime: "Allocation Blocks",
  showOnTeeSheet: "Allocation Blocks",
  closed: "Allocation Blocks",
  playerType: "Player Types",
  daysOutStart: "Player Types",
  daysOutEnd: "Player Types",
  guaranteePlayerType: "Player Types",
  availableForWebPlayerType: "Player Types",
  rateType: "Rate Types",
  guaranteeRateType: "Rate Types",
  suppressOnReceipt: "Rate Types",
  availableOnWebRateType: "Rate Types",
  minPlayers: "Rate Types",
  allocationBlock: "Allocation Block/ Player Type Mapping",
  startDate: "Tee Time Interval Setup",
  endDate: "Tee Time Interval Setup",
  startTime: "Tee Time Interval Setup",
  endTime: "Tee Time Interval Setup",
  minMinutes: "Tee Time Interval Setup",
  availableDaysInAWeek: "Rate Plan",
  holeNumber: "Rain Check Setup",
  greenFeePer: "Rain Check Setup",
  cartFeePer: "Rain Check Setup",
  greenFee: "Rate Plan",
  cartFee: "Rate Plan",
  roleCode: "Role Setup",
  roleName: "Role Setup",
  categoryGroup: "Category Groups",
  categoryName: "Retail Categories",
  eligibleForDiscount: "Retail Categories",
  eligibleForCostOrCostPlus: "Retail Categories",
  eligibleForInHouseServiceChargeCalculation: "Retail Categories",
  subCategoryName: "Retail Sub Categories",
  taxName: "Taxes",
  allowTaxonServiceCharge: "Taxes",
  allowTaxonGratuity: "Taxes",
  percentageOrFlatAmount: "Taxes",
  value: "Taxes",
  startDateTax: "Taxes",
  endDateTax: "Taxes",
  defaultToAllItems: "Taxes",
  hasTaxOnTax: "Taxes",
  allowRemovalForPaymentMethod: "Taxes",
  excludeForTaxExemption: "Taxes",
  discountType: "Discount Types",
  quickSaleCategory: "Quick Sale Categories",
  vendorSetupHeader: "Vendor Setup",
  vendorTypes: "Vendor Types",
  discountTypes: "Discount Types",
  startDateRatesSetup: "Rate Plan",
  endDateRatesSetup: "Rate Plan",
  startTimeRatesSetup: "Rate Plan",
  endTimeRatesSetup: "Rate Plan",
  time: "Tee Time Interval Setup",
  discountReason: "Discount Reasons",
  allowSinglePlayerTeeTime: "Tee Time Interval Setup",
  greenFeeItem: "Rate Plan",
  cartFeeItem: "Rate Plan",
  entryFee: "Rate Plan",
  entryFeeItem: "Rate Plan",
  propertyInformationHeader: "Property Information",
  outletSetupHeader: "Outlet Setup",
  courseHeader: "Courses",
  allocationBlockHeader: "Allocation Blocks",
  playerTypeHeader: "Player Types",
  rateTypesHeaders: "Rate Types",
  playerAllocationBlockHeader: "Allocation Block/ Player Type Mapping",
  teeTimeIntervalHeader: "Tee Time Interval Setup",
  rainCheckSetupHeader: "Rain Check Setup",
  roleSetupHeader: "Role Setup",
  categoryGroupsHeader: "Category Groups",
  retailCategoryHeaders: "Retail Categories",
  retailSubCategoryHeaders: "Retail Sub Categories",
  taxesHeaders: "Taxes",
  discountTypesHeaders: "Discount Types",
  quickSaleHeader: "Quick Sale Categories",
  vendorTypeHeader: "Vendor Types",
  discountReasonsHeader: "Discount Reasons",
  ratesSetupHeader: "Rates Setup",
  ratePlanHeader: "Rate Plan",
};

/**
 * Spa fields are prefixed by screen (e.g. outletSetupOutletName, serviceLocation…).
 * Use singular prefixes that match TooltipInformation keys. Longest match wins.
 */
const SPA_FIELD_PREFIX_SCREENS: [string, string][] = (
  [
    ["propertyInformation", "Property Information"],
    ["serviceMedicalCondition", "Service Medical Conditions"],
    ["serviceMedicalConditions", "Service Medical Conditions"],
    ["serviceLocation", "Service Locations"],
    ["serviceLocations", "Service Locations"],
    ["serviceEquipment", "Service Equipment"],
    ["serviceItemNumber", "Item Numbers for Services"],
    ["serviceGroup", "Service Groups"],
    ["therapistSetup", "Therapist Setup"],
    ["outletSetup", "Outlet Setup"],
    ["roleSetup", "Role Setup"],
    ["cancelReason", "Cancel Reasons"],
    ["cancelReasons", "Cancel Reasons"],
    ["noShowReason", "No Show Reasons"],
    ["noShowReasons", "No Show Reasons"],
    ["breakType", "Break Types"],
    ["breakTypes", "Break Types"],
    ["lostDeniedReason", "Lost Denied Reasons"],
    ["lostDeniedReasons", "Lost Denied Reasons"],
    ["logType", "Log Types"],
    ["logTypes", "Log Types"],
    ["guestType", "Guest Types"],
    ["guestTypes", "Guest Types"],
    ["priceType", "Price Types"],
    ["priceTypes", "Price Types"],
    ["dayPackageItemNumber", "Item Numbers for Day Packages"],
    ["dayPackage", "Packages"],
    ["dayPackages", "Packages"],
    ["addOnItemNumber", "Item Numbers for Add Ons"],
    ["addOn", "Add Ons"],
    ["addOns", "Add Ons"],
    ["categoryGroup", "Category Groups"],
    ["categoryGroups", "Category Groups"],
    ["categoryLinking", "Category Linking"],
    ["retailCategory", "Retail Categories"],
    ["retailCategories", "Retail Categories"],
    ["retailSubCategory", "Retail Sub Categories"],
    ["retailSubCategories", "Retail Sub Categories"],
    ["retailItem", "Retail Items Setup"],
    ["paymentMethod", "Payment Methods"],
    ["paymentMethods", "Payment Methods"],
    ["vendorTypes", "Vendor Types"],
    ["vendorSetup", "Vendor Setup"],
    ["vendor", "Vendor Setup"],
    ["discountReason", "Discount Reasons"],
    ["discountReasons", "Discount Reasons"],
    ["discountType", "Discount Types"],
    ["discountTypes", "Discount Types"],
    ["quickSale", "Quick Sale Categories"],
    ["itemNumbersForDayPackages", "Item Numbers for Day Packages"],
    ["itemNumbersForAddOns", "Item Numbers for Add Ons"],
    ["itemNumbersForServices", "Item Numbers for Services"],
    ["services", "Services"],
    ["service", "Services"],
    ["tax", "Taxes"],
  ] as [string, string][]
).sort((a, b) => b[0].length - a[0].length);

export function spaScreenAndFieldFromKey(fieldKey: string): {
  screen: string;
  fieldPart: string;
} {
  for (const [prefix, screen] of SPA_FIELD_PREFIX_SCREENS) {
    if (fieldKey === `${prefix}Header` || fieldKey === `${prefix}Headers`) {
      return { screen, fieldPart: "Header" };
    }
    if (fieldKey === prefix) {
      return { screen, fieldPart: "Header" };
    }
    if (
      fieldKey.startsWith(prefix) &&
      fieldKey.length > prefix.length &&
      /[A-Z]/.test(fieldKey.charAt(prefix.length))
    ) {
      const rest = fieldKey.slice(prefix.length);
      return {
        screen,
        fieldPart: rest || "Header",
      };
    }
  }
  if (fieldKey.endsWith("Header") || fieldKey.endsWith("Headers")) {
    return { screen: "Spa", fieldPart: fieldKey };
  }
  return { screen: "Spa", fieldPart: fieldKey };
}

export function resolveTooltipScreenLabel(
  groupKey: string,
  fieldKey: string,
): string | null {
  if (groupKey === "golfTooltipHeaders") {
    return GOLF_TOOLTIP_SCREEN_BY_FIELD[fieldKey] ?? "Golf";
  }
  if (groupKey === "spaTooltipHeaders") {
    return spaScreenAndFieldFromKey(fieldKey).screen;
  }
  if (groupKey === "customerSegmentsTooltip") {
    return VERSA_SEGMENT_SCREEN_BY_FIELD[fieldKey] ?? "Customer Segments";
  }
  if (TOOLTIP_GROUP_SCREEN_LABEL[groupKey]) {
    return TOOLTIP_GROUP_SCREEN_LABEL[groupKey];
  }
  return null;
}

/** Prefer field suffix after screen prefix (Spa) or strip Segment suffixes. */
const SNC_MANAGERS_FIELD_LABELS: Record<string, string> = {
  header: "Page header",
  managerGroupNumber: "MANAGER_GROUP_NUMBER",
  mappingCode: "MAPPING_CODE",
  managerType: "MANAGER_TYPE",
  isActive: "ISACTIVE",
  managerFirstName: "MANAGER_FIRSTNAME",
  managerLastName: "MANAGER_LASTNAME",
};

export function resolveTooltipDisplayFieldKey(
  groupKey: string,
  fieldKey: string,
): string {
  if (groupKey === "spaTooltipHeaders") {
    return spaScreenAndFieldFromKey(fieldKey).fieldPart;
  }
  if (groupKey === "customerSegmentsTooltip") {
    if (fieldKey === "header1" || fieldKey === "header2") return "Page header";
    if (fieldKey === "active1" || fieldKey === "active2") return "Active";
    if (fieldKey === "guestTypeCode2") return "Guest Type Code";
  }
  if (groupKey === "sncManagersTooltip") {
    return SNC_MANAGERS_FIELD_LABELS[fieldKey] ?? fieldKey;
  }
  return fieldKey;
}
